import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AxiosError } from 'axios'

import showErrorToast from '@/common/utils/showErrorToast'
import Spinner from '@/common/components/Spinner'
import CommissionForm from '../../../CommissionForm'
import { CommissionFormValues } from '../../../CommissionForm/components/FormSchema'
import useQueryGetCommissionById from '@/modules/promotion/api/hooks/useQueryGetCommissionById'
import useMutationUpdateCommission from '@/modules/promotion/api/hooks/useMutateUpdateCommission'
import { CreateCommissionCashbackRequest } from '@/modules/promotion/type'
import { useQueryClient } from '@tanstack/react-query'
import { CommissionCashbackQueryKeys } from '@/modules/promotion/keys'
import { ProviderResolutionConflictDialog } from '../../../ProviderResolutionConflictDialog'

export default function EditCommissionForm() {
	const navigate = useNavigate()
	const { id } = useParams()
	const { data: commission, isLoading } = useQueryGetCommissionById(id!)
	const queryClient = useQueryClient()
	const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
	const [conflictMessage, setConflictMessage] = useState('')

	const { mutate: updateCommission } = useMutationUpdateCommission({
		onSuccess: () => {
			toast.success('แก้ไขโปรโมชั่นสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [CommissionCashbackQueryKeys.GetCommissionCashbackById, id],
			})
			navigate('/operator/commission')
		},
		onError: (error) => {
			const axiosError = error as unknown as AxiosError<{
				message: string
				errorCode: string
			}>

			// Check if it's a provider resolution conflict error
			if (
				axiosError.isAxiosError &&
				axiosError.response?.data?.errorCode === 'PROVIDER_RESOLUTION_CONFLICT'
			) {
				setConflictMessage(axiosError.response.data.message)
				setConflictDialogOpen(true)
				return
			}

			// For other errors, show toast
			showErrorToast(error)
		},
	})

	const handleSubmit = (payload: CreateCommissionCashbackRequest) => {
		updateCommission({
			id: id!,
			payload,
		})
	}

	if (isLoading || !commission) return <Spinner />

	return (
		<>
			<CommissionForm
				mode="edit"
				defaultValues={commission as unknown as Partial<CommissionFormValues>}
				onSubmit={handleSubmit}
			/>
			<ProviderResolutionConflictDialog
				isOpen={conflictDialogOpen}
				onClose={() => setConflictDialogOpen(false)}
				message={conflictMessage}
			/>
		</>
	)
}
